import {
  events,
  EventsInterface,
  getErrors,
  loading,
  setChannels,
  setCommittee,
  setCommittees,
  setCustomerData,
  setEvent,
  setOnEventCreated,
  setOnEventDeleted,
  setOnEventUpdated,
  setOnSuccessSubmit,
  setPaginator,
  setPermits,
  setSocialMedia,
  setSocialMedias,
  setStudios,
  setTickets,
  setTicketScanned,
} from '@/reducers/events';
import { headers } from '@/services/headers';
import { ThunkAction } from '@reduxjs/toolkit';
import axios from 'axios';
import moment from 'moment';
import toast from 'react-hot-toast';
import { RootState } from '../store';

export const GetEvents =
  (query?: string | null): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .get(
        process.env.REACT_APP_BASE_URL + '/org/events?filter[search]=' + query,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(events(response.data.data));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const ShowEvent =
  (code?: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .get(process.env.REACT_APP_BASE_URL + '/org/events/' + code, {
        headers: headers(),
      })
      .then((response) => {
        dispatch(setEvent(response.data.data));

        if (response.data.data.type === 'cinema') {
          dispatch(setStudios(response.data.data.studios));
        } else {
          dispatch(setTickets(response.data.data.tickets));
        }

        dispatch(setChannels(response.data.data.channels));
        dispatch(setCustomerData(response.data.data.customer_data));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const UpdateEventInformation =
  (
    eventId: string,
    data: EventsInterface
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .patch(
        process.env.REACT_APP_BASE_URL +
          '/org/events/' +
          eventId +
          '/information',
        {
          title: data.title,
          description: data.description,
          terms: data.terms,
          start_at: moment(data.start_at).format('YYYY-MM-DD HH:mm'),
          end_at: moment(data.end_at).format('YYYY-MM-DD HH:mm'),
          type: data.type,
          locations: {
            name: data.location?.name,
            url: data.location?.url,
            type: data.location?.type,
          },
        },
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setOnSuccessSubmit('information-updated'));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const UpdateEventStatus =
  (
    eventId: string,
    status: string
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .patch(
        process.env.REACT_APP_BASE_URL + '/org/events/' + eventId,
        {
          status: status,
        },
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setOnEventUpdated(Math.random()));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const UploadBanner =
  (eventId: string, image: File): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .post(
        process.env.REACT_APP_BASE_URL + '/org/events/' + eventId + '/banner',
        {
          image: image,
        },
        {
          headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
        }
      )
      .then((response) => {})
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const CreateEvent =
  (title: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .post(
        process.env.REACT_APP_BASE_URL + '/org/events',
        {
          title: title,
        },
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setOnEventCreated(response.data.data.code));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const DeleteEvent =
  (eventId: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .delete(process.env.REACT_APP_BASE_URL + '/org/events/' + eventId, {
        headers: headers(),
      })
      .then((response) => {
        dispatch(setOnEventDeleted());
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const PublishEvent =
  (eventId: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .patch(
        process.env.REACT_APP_BASE_URL + '/org/events/' + eventId + '/publish',
        {},
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setOnSuccessSubmit('event-published'));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const ScanTicket =
  (
    eventId: number,
    qrCode: string
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .post(
        process.env.REACT_APP_BASE_URL + `/org/events/${eventId}/scan-ticket`,
        {
          qr_code: qrCode,
        },
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setTicketScanned(response.data.data));

        dispatch(loading(false));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const GetCommittees =
  (
    slug?: string,
    query?: string | null,
    page = 1
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .get(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${slug}/committees?page=${page}&filter[search]=` +
          query,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setCommittees(response.data.data));
        dispatch(setPaginator(response.data.meta));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const DeleteCommittees =
  (
    eventId: number,
    committeeId: number
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .delete(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${eventId}/committees/${committeeId}`,
        {
          headers: headers(),
        }
      )
      .then(() => {
        dispatch(setCommittee({ deleted: committeeId }));
        dispatch(loading(false));

        toast.success(`Berhasil menghapus panitia!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const StoreCommittee =
  (
    eventId: number,
    formData: any
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .post(
        process.env.REACT_APP_BASE_URL + `/org/events/${eventId}/committees`,
        formData,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setCommittee(response.data.data));
        dispatch(loading(false));

        toast.success(`Berhasil menambahkan panitia!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const UpdateCommittee =
  (
    eventId: number,
    committeeId: number,
    formData: any
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .patch(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${eventId}/committees/${committeeId}`,
        formData,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setCommittee(response.data.data));
        dispatch(loading(false));

        toast.success(`Berhasil mengubah panitia!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const UpdateAdminFee =
  (
    eventId: number,
    formData: any
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .patch(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${eventId}/settings/admin-fee`,
        formData,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(loading(false));

        toast.success(`Berhasil mengubah biaya admin!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const GetPermits =
  (
    slug?: string,
    query?: string | null,
    page = 1
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .get(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${slug}/settings/documents?page=${page}&filter[search]=` +
          query,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setPermits(response.data.data));
        dispatch(setPaginator(response.data.meta));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const GetSocialMedias =
  (
    slug?: string,
    query?: string | null,
    page = 1
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    const response = axios
      .get(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${slug}/marketing/social-medias?page=${page}&filter[search]=` +
          query,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setSocialMedias(response.data.data));
        dispatch(setPaginator(response.data.meta));
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );
      });

    dispatch(loading(false));

    return response;
  };

export const StoreSocialMedia =
  (
    eventId: number,
    formData: any
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .post(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${eventId}/marketing/social-media`,
        formData,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setSocialMedia(response.data.data));
        dispatch(loading(false));

        toast.success(`Berhasil menambahkan media sosial!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };

export const UpdateSocialMedia =
  (
    eventId: number,
    socialMediaId: number,
    formData: any
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    dispatch(loading(true));

    return axios
      .patch(
        process.env.REACT_APP_BASE_URL +
          `/org/events/${eventId}/marketing/social-media/${socialMediaId}`,
        formData,
        {
          headers: headers(),
        }
      )
      .then((response) => {
        dispatch(setSocialMedia(response.data.data));
        dispatch(loading(false));

        toast.success(`Berhasil mengubah media sosial!`);
      })
      .catch((error: any) => {
        const { errors, message } = error.response.data.error;

        dispatch(
          getErrors({
            errorMsg: message,
            errors: errors,
          })
        );

        dispatch(loading(false));
      });
  };
